/**
 * Policy templates: the rules every surface shares — which files a template
 * may be, how a blank left to fill in is recognised, which templates an
 * organisation sees — and the wire contracts the domain API, the workspace
 * proxy and the web client agree on.
 *
 * A template is a starting point, never a policy. A firm starts a policy from
 * one, eve tailors it from the firm's own profile, and the finished document
 * goes into the library through the ordinary PDF upload. The regulators we
 * serve warn against generic, untailored documents (JFSC, IOM FSA, SRA, HMRC
 * all say so in terms), which is why filing a document drafted from a
 * template is refused while any blank is left in it: {@link findTemplatePlaceholders}
 * is that check, run by the domain API on the filed PDF.
 *
 * Two kinds of template share one shape:
 *
 * - An organisation's own: uploaded by its admins, or saved from one of its
 *   policies. Only that organisation sees it.
 * - A global one: published by a regulator or written by Amoreg, shown to every
 *   organisation whose jurisdictions it covers. A template whose licence
 *   forbids copying (`link_only`) carries no file, only the link to the
 *   original — Amoreg never stores a copy of it.
 *
 * @module shared/policyTemplates
 */
import * as Schema from "effect/Schema";

import { fileExtension } from "./knowledgeFiles.ts";
import { UploadFilename } from "./policyContracts.ts";

export { PolicyTemplateOrigin } from "./policyContracts.ts";

// ── Files ───────────────────────────────────────────────────────────────

export type PolicyTemplateFormat = "pdf" | "docx" | "xlsx";

export interface PolicyTemplateFileType {
  /** Lower-case, with the dot. */
  readonly extension: string;
  readonly format: PolicyTemplateFormat;
  /** What the file is, for a person. */
  readonly label: string;
  /** Sent when the bytes are stored and served — never inferred from them. */
  readonly contentType: string;
}

/**
 * What a template may be. Regulators publish almost every template as Word or
 * Excel, so both are accepted alongside PDF; the policy drafted from one is
 * always a PDF. Legacy `.doc`/`.xls` are left out: nothing in the sandbox
 * fills them reliably, and every publisher that still offers one also offers
 * the modern format.
 */
export const POLICY_TEMPLATE_FILE_TYPES: ReadonlyArray<PolicyTemplateFileType> = [
  { extension: ".pdf", format: "pdf", label: "PDF", contentType: "application/pdf" },
  {
    extension: ".docx",
    format: "docx",
    label: "Word",
    contentType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  {
    extension: ".xlsx",
    format: "xlsx",
    label: "Excel",
    contentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  },
];

/** Same ceiling as a policy upload: the API's JSON body carries the file as base64. */
export const POLICY_TEMPLATE_MAX_BYTES = 25 * 1024 * 1024;

/** For a file input's `accept`. */
export const POLICY_TEMPLATE_ACCEPT = POLICY_TEMPLATE_FILE_TYPES.map((type) => type.extension).join(
  ",",
);

/** The file type of a template name, or null when templates cannot be that type. */
export const policyTemplateFileType = (filename: string): PolicyTemplateFileType | null =>
  POLICY_TEMPLATE_FILE_TYPES.find((type) => type.extension === fileExtension(filename)) ?? null;

/**
 * Why a file cannot be a template, or null when it can. One wording for the
 * browser's pre-check and the API's guard.
 */
export const policyTemplateFileProblem = (file: {
  readonly name: string;
  readonly size: number;
}): string | null =>
  policyTemplateFileType(file.name) === null
    ? "Templates must be PDF, Word (.docx) or Excel (.xlsx) files."
    : file.size === 0
      ? "The file is empty."
      : file.size > POLICY_TEMPLATE_MAX_BYTES
        ? `Templates are limited to ${POLICY_TEMPLATE_MAX_BYTES / (1024 * 1024)} MB.`
        : null;

// ── Blanks ──────────────────────────────────────────────────────────────

/** Most blanks reported for one document; past this the count is what matters. */
export const MAX_REPORTED_PLACEHOLDERS = 50;

/**
 * Words a bracketed blank starts with in real templates: "[Insert date]",
 * "[Name of MLRO]", "[Firm name]". Matched on the first word only, so a
 * bracketed citation ("[Rule 4.2]", "[2026] EWCA Civ 12") is left alone.
 */
const BLANK_LEAD_WORDS = new Set([
  "insert",
  "enter",
  "add",
  "specify",
  "state",
  "describe",
  "include",
  "name",
  "date",
  "firm",
  "company",
  "entity",
  "organisation",
  "organization",
  "client",
  "address",
  "title",
  "role",
  "number",
  "amount",
  "position",
  "department",
  "jurisdiction",
  "owner",
  "signature",
  "tbc",
  "tbd",
]);

/**
 * Words that mark an all-capitals blank wherever they sit in it: "[MLRO
 * NAME]", "[COMPANY FULL NAME]", "[EFFECTIVE DATE]". An all-capitals
 * reference without one — "[UK GDPR]", "[FCA]", "[SYSC]" — is finished text.
 */
const BLANK_WORDS = new Set([
  ...BLANK_LEAD_WORDS,
  "details",
  "email",
  "phone",
  "telephone",
  "here",
  "value",
  "percentage",
  "period",
  "frequency",
  "officer",
  "manager",
  "director",
  "person",
  "contact",
]);

/** A date format spelt out as the blank itself: "DD/MM/YYYY", "MM-YY", "YYYY". */
const DATE_FORMAT_BLANK = /^(?:dd|mm|yy|yyyy)(?:\s*[/.-]\s*(?:dd|mm|yy|yyyy))*$/i;

const isBlankInBrackets = (inner: string): boolean => {
  const content = inner.trim().replace(/\s+/g, " ");
  if (content.length === 0) return false;
  // Runs of underscores, dots or ellipses are blanks whatever surrounds them: "[__%]", "[……]".
  if (/_{2,}|\.{3,}|…/.test(content)) return true;
  // A bare number or a year is a citation or a footnote, never a blank.
  if (!/[A-Za-z]/.test(content)) return false;
  if (DATE_FORMAT_BLANK.test(content)) return true;
  const words = content
    .split(/[\s/:,()-]+/)
    .map((word) => word.toLowerCase())
    .filter((word) => word.length > 0);
  const letters = content.replace(/[^A-Za-z]/g, "");
  // "[COMPANY FULL NAME]", "[NAME]", "[MLRO NAME]" — but not "[UK GDPR]".
  if (letters.length >= 2 && letters === letters.toUpperCase()) {
    return words.some((word) => BLANK_WORDS.has(word));
  }
  return BLANK_LEAD_WORDS.has(words[0] ?? "");
};

/**
 * "[Drafting note: …]", the guidance Amoreg's own templates carry: any length
 * up to about a page, and it may itself cite a rule in brackets ("see
 * [Rule 5.1]") — one level of nesting, each inner pair bounded. The two
 * alternatives never start with the same character, so matching stays linear.
 */
const DRAFTING_NOTE = /\[\s*drafting\s+note\s*:(?:[^[\]]|\[[^[\]]{0,200}\]){1,2000}\]/gi;
const DRAFTING_NOTE_START = /^\s*drafting\s+note\s*:/i;
const NOTE_REPORT_LENGTH = 60;

/** A drafting note as it is listed: whitespace collapsed, cut short with an ellipsis. */
const shortenedNote = (note: string): string => {
  const flat = note.replace(/\s+/g, " ");
  return flat.length <= NOTE_REPORT_LENGTH
    ? flat
    : `${flat.slice(0, NOTE_REPORT_LENGTH - 2).trimEnd()}…]`;
};

/**
 * The blanks left to fill in a document's text, in the order they first
 * appear, each once. Recognises the conventions real templates use: square
 * brackets around a blank (`[INSERT NAME]`, `[Entity]`, `[DATE]`, `[__%]`),
 * runs of X (`XXX`), and the less common `<<…>>` and `{{…}}`.
 *
 * Deliberately conservative: a bracketed citation or footnote marker is not a
 * blank, because a false positive here refuses to file a finished policy.
 */
export const findTemplatePlaceholders = (text: string): ReadonlyArray<string> => {
  const found: Array<{ readonly at: number; readonly value: string }> = [];
  // Line breaks are allowed inside a marker: a PDF wraps a long blank such as
  // "[INSERT FULL\nCOMPANY NAME]" across lines, and it is still one blank.
  for (const match of text.matchAll(/\[([^[\]]{1,80})\]/g)) {
    if (DRAFTING_NOTE_START.test(match[1] ?? "")) continue;
    if (isBlankInBrackets(match[1] ?? "")) found.push({ at: match.index ?? 0, value: match[0] });
  }
  // A drafting note is guidance for the firm, often a whole paragraph long, so
  // it is matched without the 80-character limit and reported shortened.
  for (const match of text.matchAll(DRAFTING_NOTE)) {
    found.push({ at: match.index ?? 0, value: shortenedNote(match[0]) });
  }
  for (const match of text.matchAll(/<<[^<>]{1,80}>>|\{\{[^{}]{1,80}\}\}/g)) {
    found.push({ at: match.index ?? 0, value: match[0] });
  }
  // Whole-word runs of three or more capital X: "XXX", "XXXX". Not "XXL", not "Xx".
  for (const match of text.matchAll(/(?<![A-Za-z0-9])X{3,}(?![A-Za-z0-9])/g)) {
    found.push({ at: match.index ?? 0, value: match[0] });
  }
  found.sort((left, right) => left.at - right.at);
  const unique: string[] = [];
  const seen = new Set<string>();
  for (const { value } of found) {
    const key = value.replace(/\s+/g, " ");
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(key);
    if (unique.length >= MAX_REPORTED_PLACEHOLDERS) break;
  }
  return unique;
};

/** The refusal a person (or eve) sees when a drafted policy still has blanks. */
export const unfilledPlaceholdersMessage = (placeholders: ReadonlyArray<string>): string => {
  const shown = placeholders.slice(0, 10).join(", ");
  const more = placeholders.length > 10 ? ` and ${placeholders.length - 10} more` : "";
  return `This document was drafted from a template and still has blanks to fill in: ${shown}${more}. Fill or remove every blank, then file it again.`;
};

// ── Who sees what ───────────────────────────────────────────────────────

export type PolicyTemplateSource = "regulator" | "amoreg" | "firm";
export type PolicyTemplateLicence = "open" | "link_only" | "firm_owned";
export type PolicyTemplateStatus = "draft" | "published" | "archived";

/** The fields visibility is decided on. */
export interface PolicyTemplateScope {
  readonly organizationId: string | null;
  readonly status: PolicyTemplateStatus;
  readonly jurisdictionSlugs: ReadonlyArray<string>;
}

/**
 * Whether an organisation sees a template. Its own templates, unless archived;
 * a global template only once published, and only when it covers one of the
 * organisation's jurisdictions — a template tagged with none applies anywhere.
 * The organisation's jurisdictions are read from its adoption, never assumed.
 */
export const policyTemplateVisibleTo = (
  template: PolicyTemplateScope,
  organization: { readonly id: string; readonly jurisdictionSlugs: ReadonlyArray<string> },
): boolean => {
  if (template.organizationId !== null) {
    return template.organizationId === organization.id && template.status !== "archived";
  }
  if (template.status !== "published") return false;
  if (template.jurisdictionSlugs.length === 0) return true;
  const adopted = new Set(organization.jurisdictionSlugs);
  return template.jurisdictionSlugs.some((slug) => adopted.has(slug));
};

// ── Wire contracts ──────────────────────────────────────────────────────

export const PolicyTemplateFormatSchema = Schema.Literals(["pdf", "docx", "xlsx"]);
export const PolicyTemplateSourceSchema = Schema.Literals(["regulator", "amoreg", "firm"]);
export const PolicyTemplateLicenceSchema = Schema.Literals(["open", "link_only", "firm_owned"]);

export const PolicyTemplate = Schema.Struct({
  id: Schema.String,
  /** Null for a global template; the organisation's id for its own. */
  organizationId: Schema.NullOr(Schema.String),
  title: Schema.String,
  description: Schema.NullOr(Schema.String),
  /** A short type, e.g. "AML/CFT policy", used to match a missing document. */
  policyType: Schema.NullOr(Schema.String),
  documentKind: Schema.Literals(["policy", "procedure"]),
  jurisdictionSlugs: Schema.Array(Schema.String),
  source: PolicyTemplateSourceSchema,
  /** Who published it: "ICO", "SRA", the firm's own name. */
  publisher: Schema.NullOr(Schema.String),
  /** The original, for a regulator's template; always present for `link_only`. */
  sourceUrl: Schema.NullOr(Schema.String),
  licence: PolicyTemplateLicenceSchema,
  licenceNote: Schema.NullOr(Schema.String),
  /** Null for a `link_only` template: there is no stored copy. */
  filename: Schema.NullOr(Schema.String),
  format: Schema.NullOr(PolicyTemplateFormatSchema),
  sizeBytes: Schema.NullOr(Schema.Number),
  /** Blanks found in the file when it was stored. */
  placeholders: Schema.Array(Schema.String),
  status: Schema.Literals(["draft", "published", "archived"]),
  version: Schema.Number,
  /** The policy it was saved from, when it was. */
  derivedFromPolicyId: Schema.NullOr(Schema.String),
  signedOffBy: Schema.NullOr(Schema.String),
  signedOffAt: Schema.NullOr(Schema.String),
  createdAt: Schema.String,
  updatedAt: Schema.String,
  /** Download path on this API — null when there is no file. */
  downloadPath: Schema.NullOr(Schema.String),
});
export type PolicyTemplate = typeof PolicyTemplate.Type;

export const PolicyTemplateList = Schema.Struct({
  templates: Schema.Array(PolicyTemplate),
  /** Whether the viewer may add and archive the organisation's own templates. */
  canManage: Schema.Boolean,
});
export type PolicyTemplateList = typeof PolicyTemplateList.Type;

const TemplateTitle = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200));
const TemplateDescription = Schema.String.check(Schema.isMaxLength(2_000));
const TemplateType = Schema.String.check(Schema.isMaxLength(120));

export const PolicyTemplateUpload = Schema.Struct({
  filename: UploadFilename.check(Schema.isMinLength(1), Schema.isMaxLength(300)),
  contentBase64: Schema.String.check(Schema.isMinLength(1)),
  title: TemplateTitle,
  description: Schema.optional(Schema.NullOr(TemplateDescription)),
  policyType: Schema.optional(Schema.NullOr(TemplateType)),
  documentKind: Schema.optional(Schema.Literals(["policy", "procedure"])),
  /** Empty or absent: the template is for every one of the organisation's jurisdictions. */
  jurisdictionSlugs: Schema.optional(Schema.Array(Schema.String)),
});
export type PolicyTemplateUpload = typeof PolicyTemplateUpload.Type;

export const PolicyTemplateFromPolicy = Schema.Struct({
  policyId: Schema.String.check(Schema.isMinLength(1)),
  /** Absent: the policy's own title. */
  title: Schema.optional(TemplateTitle),
  description: Schema.optional(Schema.NullOr(TemplateDescription)),
  policyType: Schema.optional(Schema.NullOr(TemplateType)),
});
export type PolicyTemplateFromPolicy = typeof PolicyTemplateFromPolicy.Type;
